import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Check } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { cn } from '@/lib/utils'

const languageNames: Record<SupportedLanguage, string> = {
  it: 'IT',
  en: 'EN',
  es: 'ES',
  de: 'DE',
  fr: 'FR',
}

const languageFullNames: Record<SupportedLanguage, string> = {
  it: 'Italiano',
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
}

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
  isScrolled?: boolean
}

export function LanguageSelector({
  variant = 'desktop',
  isScrolled = false,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentLang = (i18n.language || 'en') as SupportedLanguage

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 100)
  }

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) {
      setIsOpen(false)
      return
    }

    // Get current path without language prefix
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const currentPath = pathSegments.slice(1).join('/') // Remove language prefix

    // Find matching route key and get equivalent in new language
    let newPath = ''
    for (const [, routes] of Object.entries(localizedRoutes)) {
      if (routes[currentLang] === currentPath) {
        newPath = routes[newLang]
        break
      }
    }

    // If no match found, keep the same path
    if (!newPath && currentPath) {
      newPath = currentPath
    }

    setIsOpen(false)
    i18n.changeLanguage(newLang)
    navigate(`/${newLang}${newPath ? `/${newPath}` : ''}`)
  }

  // Mobile variant - horizontal list of buttons
  if (variant === 'mobile') {
    return (
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="flex gap-2">
          {supportedLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              className={cn(
                'px-3 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200',
                lang === currentLang
                  ? 'bg-[#003b7a] text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {languageNames[lang]}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Desktop variant - hover dropdown (same pattern as Products in Header)
  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200',
          isScrolled
            ? 'text-gray-700 hover:bg-gray-100'
            : 'text-white hover:bg-white/10',
          isOpen && (isScrolled ? 'bg-gray-100' : 'bg-white/10')
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="min-w-[20px] text-center">{languageNames[currentLang]}</span>
        <ChevronDown
          size={14}
          className={cn(
            'transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Menu */}
      <div
        className={cn(
          'absolute right-0 top-full mt-2 min-w-[160px] bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 transition-all duration-200',
          isOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible translate-y-2'
        )}
        role="listbox"
      >
        {supportedLanguages.map((lang) => (
          <button
            key={lang}
            onClick={() => switchLanguage(lang)}
            className={cn(
              'lang-dropdown-item w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors duration-150',
              lang === currentLang
                ? 'text-[#003b7a] bg-[#003b7a]/5 font-semibold active'
                : 'text-gray-700'
            )}
            role="option"
            aria-selected={lang === currentLang}
          >
            <span className="flex items-center gap-3">
              <span
                className={cn(
                  'w-6 h-6 rounded flex items-center justify-center text-xs font-bold',
                  lang === currentLang
                    ? 'bg-[#003b7a] text-white'
                    : 'bg-gray-100 text-gray-600'
                )}
              >
                {languageNames[lang]}
              </span>
              <span>{languageFullNames[lang]}</span>
            </span>
            {lang === currentLang && (
              <Check size={14} className="text-[#003b7a]" />
            )}
          </button>
        ))}
      </div>

      {/* Language Dropdown Styles */}
      <style>{`
        .lang-dropdown-item {
          position: relative;
          transition: all 0.15s ease;
        }
        .lang-dropdown-item:hover {
          background-color: rgba(0, 163, 204, 0.08);
        }
        .lang-dropdown-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%) scaleY(0);
          width: 3px;
          height: 24px;
          background: linear-gradient(180deg, #00a3cc 0%, #0077b3 100%);
          border-radius: 0 4px 4px 0;
          transition: transform 0.2s ease;
        }
        .lang-dropdown-item:hover::before,
        .lang-dropdown-item.active::before {
          transform: translateY(-50%) scaleY(1);
        }
      `}</style>
    </div>
  )
}
